import type { TPlayer } from 'video.js'
import type { SettingsMenuContainer } from '../settings-menu-container'

import { Events } from '|>/shared/events'
import { on, register } from '|>/shared/vjs'
import { BaseComponent } from '../../base'
import { AudioTrackButton } from './audio-track-button'
import { setPreferableAudioTrack } from './audio-track-selection'

/**
 * Settings audio menu with available audio tracks
 */
@register
export class SettingsAudioMenu extends BaseComponent {
  declare options_: any
  declare container: SettingsMenuContainer | undefined

  trackButton_: AudioTrackButton
  menu: AudioTrackButton['menu'] | undefined

  loadeddata: boolean = false

  @on(Events.Media.DolbyConfiguration)
  private dolbyConfig: Events.Media.DolbyConfiguration

  constructor(player: TPlayer, options: any) {
    super(player, options)

    this.trackButton_ = new AudioTrackButton(player, {
      title: this.localize(options.title),
      getContainer: () => this.container,
    })

    this.handleNewTracks = this.handleNewTracks.bind(this)

    const audioTracks = this.player_.audioTracks()
    // Handle case where audio menu opened before tracks are fully loaded by player
    audioTracks?.addEventListener('addtrack', this.handleNewTracks)

    this.on('dispose', () => {
      audioTracks?.removeEventListener('addtrack', this.handleNewTracks)
    })
  }

  override contentEl() {
    // return _parent_'s container element, so any child, added to this component,
    // will be added to the parent container's element instead
    if (!this.container) throw new Error('container is not set')
    return this.container.contentEl()
  }

  override handleLanguagechange() {
    this.trackButton_.title(this.localize(this.options_.title))
  }

  handleNewTracks() {
    this.update()
  }

  @on(Events.Media.Load)
  onNewSource() {
    this.loadeddata = false
  }

  @on('loadeddata')
  onLoadedData() {
    this.loadeddata = true
    this.preferManualSelect()
  }

  preferManualSelect() {
    if (!this.loadeddata) return

    const { dolbyPlugin, isHls } = this.player_ as any

    setPreferableAudioTrack({
      player: this.player_,
      audioTracks: this.player_.audioTracks(),
      locale: this.player_.language?.() || undefined,
      preferDolbyAtmos: this.dolbyConfig?.preferDolbyAtmos,
      dolbyEnabled: this.dolbyConfig?.enabled,
      dolbyPlugin,
      isHls: Boolean(isHls),
      setTrack: (track, tracks) => this.setTrack(track, tracks),
    })
  }

  getEnabledTrack(tracks = this.player_.audioTracks()) {
    // find selected track
    let foundEnabled: any
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i]

      if (track.enabled) foundEnabled = track
    }

    return foundEnabled
  }

  setTrack(track, tracks = this.player_.audioTracks()) {
    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i]
      t.enabled = t === track
    }

    this.player_.trigger(Events.Media.AudioTracksChanged)
  }

  preselectTrack() {
    const tracks = this.player_.audioTracks()

    let enabled: any
    enabled = this.getEnabledTrack(tracks)

    // no enabled track, select the first one
    if (enabled == null) {
      this.setTrack(tracks[0], tracks)
    }
  }

  update() {
    this.preselectTrack()

    this.trackButton_.update()

    if (this.menu) {
      this.menu.dispose()
      this.removeChild(this.menu)
    }

    this.menu = this.trackButton_.menu
    this.addChild(this.menu)
  }

  activate() {
    this.update()

    if (this.menu?.el_) this.menu.addClass('vjs-x-active')
  }

  deactivate() {
    if (this.menu?.el_) this.menu.removeClass('vjs-x-active')
  }
}

SettingsAudioMenu.options = {
  createEl: false, // this component is opaque, it has no element
}
